import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Seo } from "../../../components/Seo";
import { CimmplePayMarkdown, splitLeadingH1 } from "../../../components/docs/CimmplePayMarkdown";
import {
  cimmplePayHelpDocsByRoute,
  cimmplePayHelpSidebarGroups,
  type CimmplePayHelpDocItem,
} from "../../../content/cimmplepay/helpManifest";
import { cimmplePayHelpInlineDocs } from "../../../content/cimmplepay/helpInlineDocs";
import { rewriteCimmplePayHelpLinks } from "../../../utils/rewriteCimmplePayHelpLinks";
import { stripHelpHubReadmePublishingSection } from "../../../utils/stripHelpHubReadmePublishingSection";

const MISSING_DOC = `# Page not found

We couldn’t find this help article. Use the topics on the left, or start from the [help overview](/help/cimmplepay).
`;

function withPublicUrl(path: string) {
  const base = process.env.PUBLIC_URL ?? "";
  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

function getDocForRoute(routeSlug: string): CimmplePayHelpDocItem | undefined {
  return cimmplePayHelpDocsByRoute.get(routeSlug);
}

function renderNavLink(item: CimmplePayHelpDocItem, routeSlug: string) {
  const to = item.routePath ? `/help/cimmplepay/${item.routePath}` : "/help/cimmplepay";
  const isActive = routeSlug === item.routePath;
  return (
    <li key={item.sourcePath}>
      <Link
        to={to}
        className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
          isActive
            ? "bg-primary-50 text-primary-700 dark:bg-dark-800/60 dark:text-white"
            : "text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800/40"
        }`}
      >
        {item.title}
      </Link>
    </li>
  );
}

const CimmplePayHelpExplorer: React.FC = () => {
  const params = useParams();
  const routeSlug = (params["*"] ?? "").replace(/^\/+|\/+$/g, "");
  const docItem = getDocForRoute(routeSlug);
  const [markdown, setMarkdown] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setIsLoading(true);
      if (!docItem) {
        if (isMounted) {
          setMarkdown(MISSING_DOC);
          setIsLoading(false);
        }
        return;
      }

      try {
        const sourcePath = docItem.sourcePath
          .split("/")
          .map((segment) => encodeURIComponent(segment))
          .join("/");
        const res = await fetch(withPublicUrl(`/content/cimmplepay/help/${sourcePath}`));
        if (!res.ok) throw new Error(`Failed to load ${docItem.sourcePath}`);
        const text = await res.text();
        if (isMounted) setMarkdown(text);
      } catch {
        if (isMounted) {
          const fallback = cimmplePayHelpInlineDocs[docItem.sourcePath];
          setMarkdown(fallback ?? MISSING_DOC);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [docItem]);

  const processedMarkdown = useMemo(() => {
    if (!docItem || !markdown) return markdown;
    let md = markdown;
    if (docItem.sourcePath === "README.md") {
      md = stripHelpHubReadmePublishingSection(md);
    }
    return rewriteCimmplePayHelpLinks(md, docItem.sourcePath);
  }, [markdown, docItem]);

  const { title, body } = useMemo(() => splitLeadingH1(processedMarkdown), [processedMarkdown]);
  const seoTitle = `${docItem?.title ?? "CimmplePay Help"} | Cimmple`;
  const seoDescription = `${docItem?.title ?? "CimmplePay help for U.S. employers"} — guides for payroll, taxes, and paying employees.`;

  return (
    <div className="pt-24">
      <Seo title={seoTitle} description={seoDescription} />

      <section className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <aside className="lg:col-span-1">
              <div className="card p-5 sticky top-28">
                <h2 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-1">
                  CimmplePay Help
                </h2>
                <p className="text-xs text-slate-500 dark:text-dark-400 mb-4">
                  Guides for U.S. employers running payroll. Building an integration? See the{" "}
                  <Link to="/docs/cimmplepay" className="text-primary-600 dark:text-primary-400 hover:underline">
                    CimmplePay API documentation
                  </Link>
                  .
                </p>
                <nav className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
                  <div>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-dark-400 mb-2">
                      Help home
                    </h3>
                    <ul className="space-y-1.5">
                      <li>
                        <Link
                          to="/help/cimmplepay"
                          className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                            routeSlug === ""
                              ? "bg-primary-50 text-primary-700 dark:bg-dark-800/60 dark:text-white"
                              : "text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800/40"
                          }`}
                        >
                          Overview
                        </Link>
                      </li>
                    </ul>
                  </div>
                  {cimmplePayHelpSidebarGroups.map((group) => (
                    <div key={group.label}>
                      <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-dark-400 mb-2">
                        {group.label}
                      </h3>
                      <ul className="space-y-1.5">{group.items.map((item) => renderNavLink(item, routeSlug))}</ul>
                    </div>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="lg:col-span-3">
              <div className="card p-8 md:p-12">
                {isLoading ? (
                  <p className="text-slate-600 dark:text-dark-300">Loading…</p>
                ) : (
                  <>
                    {title ? (
                      <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-6">
                        {title}
                      </h1>
                    ) : null}
                    <CimmplePayMarkdown markdown={body} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CimmplePayHelpExplorer;
